import React, { useEffect, useMemo, useRef, useState } from 'react';
import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import noDicomIcon from '../../../assets/nodicom-icon.png';

import { getDicomInstanceBlob, getDicomSeries } from '../../services/dicomService';
import type { DicomSeriesRenderInstance } from '../../types/dicom';

type CornerstoneThumbnailRenderer = {
  utilities: {
    loadImageToCanvas: (options: {
      canvas: HTMLCanvasElement;
      imageId: string;
      thumbnail: boolean;
      useCPURendering: boolean;
    }) => Promise<unknown>;
  };
  cache?: {
    getImageLoadObject: (imageId: string) => unknown;
    removeImageLoadObject: (imageId: string) => void;
  };
};

type WindowWithCornerstone = Window & {
  cornerstone?: CornerstoneThumbnailRenderer;
};

interface DicomSeriesThumbnailProps {
  uploadId: string;
  studyInstanceUid: string;
  seriesInstanceUid: string;
  token: string;
  label: string;
  interactive?: boolean;
  thumbnailDataUrls?: string[];
}

const MAX_CACHED_SLICES = 20;
const EMPTY_SERVER_THUMBNAILS: string[] = [];
export const MAX_SERIES_PREVIEW_SLICES = 10;

export function sampleDicomSeriesInstances(
  instances: DicomSeriesRenderInstance[],
  maximumSamples = MAX_SERIES_PREVIEW_SLICES
) {
  if (instances.length <= maximumSamples) {
    return instances;
  }

  return Array.from({ length: maximumSamples }, (_, sampleIndex) => {
    const sourceIndex = Math.round(
      (sampleIndex / Math.max(1, maximumSamples - 1)) * (instances.length - 1)
    );
    return instances[sourceIndex];
  });
}

function getDicomFileIndex(imageId: string) {
  const parsedIndex = Number(imageId.slice(imageId.indexOf(':') + 1));
  return Number.isInteger(parsedIndex) ? parsedIndex : null;
}

const DicomSeriesThumbnail: React.FC<DicomSeriesThumbnailProps> = ({
  uploadId,
  studyInstanceUid,
  seriesInstanceUid,
  token,
  label,
  interactive = false,
  thumbnailDataUrls = EMPTY_SERVER_THUMBNAILS,
}) => {
  const [thumbnailSrc, setThumbnailSrc] = useState<string | null>(null);
  const [instances, setInstances] = useState<DicomSeriesRenderInstance[]>([]);
  const [sliceIndex, setSliceIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const sliceCacheRef = useRef(new Map<string, string>());
  const serverThumbnails = useMemo(
    () => thumbnailDataUrls.filter(source => source.startsWith('data:image/png')),
    [thumbnailDataUrls]
  );

  useEffect(() => {
    if (serverThumbnails.length > 0) {
      const initialSliceIndex = Math.floor(serverThumbnails.length / 2);
      sliceCacheRef.current.clear();
      setInstances([]);
      setSliceIndex(initialSliceIndex);
      setThumbnailSrc(serverThumbnails[initialSliceIndex]);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    const controller = new AbortController();
    let active = true;

    const loadSeries = async () => {
      try {
        const series = await getDicomSeries(
          uploadId,
          studyInstanceUid,
          seriesInstanceUid,
          token,
          controller.signal
        );

        if (active) {
          const previewInstances = sampleDicomSeriesInstances(series.instances);
          const initialSliceIndex = Math.floor(previewInstances.length / 2);
          sliceCacheRef.current.clear();
          setInstances(previewInstances);
          setSliceIndex(initialSliceIndex);
          setHasError(series.instances.length === 0);
        }
      } catch (error) {
        if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    setThumbnailSrc(null);
    setInstances([]);
    setIsLoading(true);
    setHasError(false);
    void loadSeries();

    return () => {
      active = false;
      controller.abort();
    };
  }, [seriesInstanceUid, serverThumbnails, studyInstanceUid, token, uploadId]);

  useEffect(() => {
    if (serverThumbnails.length > 0) {
      const source = serverThumbnails[Math.min(sliceIndex, serverThumbnails.length - 1)];
      setThumbnailSrc(source ?? null);
      setIsLoading(false);
      setHasError(!source);
      return;
    }

    const instance = instances[sliceIndex];
    if (!instance) {
      return;
    }

    // Keep the rendered bitmap stable when a card becomes selected. Re-rendering
    // the same slice with a different thumbnail mode caused a visible large ->
    // small jump after the selected-card layout had already expanded.
    const cacheKey = `${sliceIndex}`;
    const cachedSlice = sliceCacheRef.current.get(cacheKey);
    if (cachedSlice) {
      setThumbnailSrc(cachedSlice);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    const controller = new AbortController();
    let active = true;
    let imageId: string | null = null;

    const renderSlice = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const dicomBlob = await getDicomInstanceBlob(
          instance.instance_url,
          token,
          controller.signal
        );
        const cornerstone = (window as WindowWithCornerstone).cornerstone;

        if (!cornerstone?.utilities?.loadImageToCanvas) {
          throw new Error('DICOM renderer is not ready.');
        }

        imageId = dicomImageLoader.wadouri.fileManager.add(dicomBlob);
        const canvas = document.createElement('canvas');
        await cornerstone.utilities.loadImageToCanvas({
          canvas,
          imageId,
          thumbnail: false,
          useCPURendering: true,
        });

        if (active) {
          const nextThumbnailSrc = canvas.toDataURL('image/png');
          const cache = sliceCacheRef.current;
          cache.set(cacheKey, nextThumbnailSrc);
          if (cache.size > MAX_CACHED_SLICES) {
            const oldestCacheKey = cache.keys().next().value;
            if (typeof oldestCacheKey === 'string') {
              cache.delete(oldestCacheKey);
            }
          }
          setThumbnailSrc(nextThumbnailSrc);
          setIsLoading(false);
        }
      } catch (error) {
        if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
          setHasError(true);
          setIsLoading(false);
        }
      } finally {
        if (imageId) {
          const fileIndex = getDicomFileIndex(imageId);
          if (fileIndex !== null) {
            dicomImageLoader.wadouri.fileManager.remove(fileIndex);
          }
          const cornerstoneCache = (window as WindowWithCornerstone).cornerstone?.cache;
          if (cornerstoneCache?.getImageLoadObject(imageId)) {
            cornerstoneCache.removeImageLoadObject(imageId);
          }
        }
      }
    };

    void renderSlice();

    return () => {
      active = false;
      controller.abort();
    };
  }, [instances, serverThumbnails, sliceIndex, token]);

  const previewSliceCount = serverThumbnails.length || instances.length;

  return (
    <div
      className={`dicom-thumbnail-content ${interactive ? 'is-interactive' : ''}`}
      data-testid={interactive ? 'interactive-series-thumbnail' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label} sampled slice preview.` : undefined}
    >
      {thumbnailSrc && (
        <img
          className="dicom-thumb-image"
          src={thumbnailSrc}
          alt={`${label} DICOM preview`}
        />
      )}

      {hasError && !thumbnailSrc && (
        <img
          className="dicom-thumb-fallback"
          src={noDicomIcon}
          alt={`${label} preview unavailable`}
          draggable={false}
        />
      )}

      {isLoading && !thumbnailSrc && (
        <span
          className="dicom-thumb-loading"
          role="status"
          aria-label={`Loading ${label} preview`}
        />
      )}

      {interactive && previewSliceCount > 0 && (
        <div
          className="dicom-thumbnail-slice-control"
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
        >
          <input
            className="dicom-thumbnail-slice-slider"
            type="range"
            min={0}
            max={Math.max(0, previewSliceCount - 1)}
            step={1}
            value={sliceIndex}
            aria-label={`${label} slice`}
            aria-valuetext={`${sliceIndex + 1} of ${previewSliceCount}`}
            onChange={event => setSliceIndex(Number(event.target.value))}
          />
          <span className="dicom-thumbnail-slice-counter">
            {sliceIndex + 1}/{previewSliceCount}
          </span>
        </div>
      )}
    </div>
  );
};

export default DicomSeriesThumbnail;
